/**
 * A modal image viewer, shared by the lab reports and the photo wall.
 *
 * Why it exists: a page of an SGS report is a document, not a picture. The old behaviour
 * put the raw PNG in a new tab, which on a phone means a 678x960 image fitted to the
 * screen with type too small to read, no way to zoom that does not also zoom the browser
 * chrome, and no way back except the tab switcher. A trust page whose evidence cannot be
 * read on the device most people are holding is not evidence.
 *
 * So: fit to the screen on open, pinch or double-tap to zoom in, drag to pan, and a page
 * you can move through without leaving the site. The original file stays one tap away.
 *
 * Progressive enhancement throughout. The markup is an ordinary <a href> to the full
 * image with target="_blank"; this intercepts the click. With no JavaScript, or if this
 * file fails, the link does exactly what it did before.
 */

const MAX_SCALE = 6;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 24;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

class BeeulViewer {
  constructor() {
    this.items = [];
    this.index = 0;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.pointers = new Map();
    this.lastTap = 0;
    this.build();
  }

  /* ---------------------------------------------------------------- build */

  build() {
    const icon = (body) =>
      `<svg class="beeul-icon" viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">${body}</svg>`;
    const chevron = icon('<path d="m9 5 7 7-7 7" />');

    const dialog = document.createElement('dialog');
    dialog.className = 'beeul-viewer';
    dialog.setAttribute('aria-label', 'Image viewer');
    dialog.innerHTML = `
      <div class="beeul-viewer__bar beeul-viewer__bar--top">
        <p class="beeul-viewer__title"></p>
        <button type="button" class="beeul-viewer__btn beeul-viewer__close" aria-label="Close viewer">
          ${icon('<path d="M6 6l12 12" /><path d="M18 6 6 18" />')}
        </button>
      </div>

      <div class="beeul-viewer__stage">
        <img class="beeul-viewer__img" alt="" draggable="false">
      </div>

      <button type="button" class="beeul-viewer__btn beeul-viewer__nav beeul-viewer__nav--prev" aria-label="Previous">${chevron}</button>
      <button type="button" class="beeul-viewer__btn beeul-viewer__nav beeul-viewer__nav--next" aria-label="Next">${chevron}</button>

      <div class="beeul-viewer__bar beeul-viewer__bar--bottom">
        <p class="beeul-viewer__caption"></p>
        <div class="beeul-viewer__tools">
          <button type="button" class="beeul-viewer__btn beeul-viewer__zoom-out" aria-label="Zoom out">${icon('<path d="M6 12h12" />')}</button>
          <output class="beeul-viewer__level" aria-live="off">100%</output>
          <button type="button" class="beeul-viewer__btn beeul-viewer__zoom-in" aria-label="Zoom in">${icon('<path d="M12 6v12" /><path d="M6 12h12" />')}</button>
          <a class="beeul-viewer__original" target="_blank" rel="noopener" aria-label="Open the original image file"><span class="beeul-viewer__original-long">Open </span>original</a>
        </div>
      </div>
    `;

    this.dialog = dialog;
    this.stage = dialog.querySelector('.beeul-viewer__stage');
    this.img = dialog.querySelector('.beeul-viewer__img');
    this.titleEl = dialog.querySelector('.beeul-viewer__title');
    this.captionEl = dialog.querySelector('.beeul-viewer__caption');
    this.levelEl = dialog.querySelector('.beeul-viewer__level');
    this.originalEl = dialog.querySelector('.beeul-viewer__original');
    this.prevBtn = dialog.querySelector('.beeul-viewer__nav--prev');
    this.nextBtn = dialog.querySelector('.beeul-viewer__nav--next');

    dialog.querySelector('.beeul-viewer__close').addEventListener('click', () => dialog.close());
    this.prevBtn.addEventListener('click', () => this.step(-1));
    this.nextBtn.addEventListener('click', () => this.step(1));
    dialog.querySelector('.beeul-viewer__zoom-in').addEventListener('click', () => this.zoomBy(1.6));
    dialog.querySelector('.beeul-viewer__zoom-out').addEventListener('click', () => this.zoomBy(1 / 1.6));

    dialog.addEventListener('keydown', (e) => this.onKey(e));

    // Teardown watches the `open` attribute rather than listening for `close`.
    //
    // The close event is the obvious hook and it is what the spec says fires, but it did
    // not arrive in testing - the dialog closed, `open` flipped, and neither the viewer's
    // listener nor a bare probe listener ran. Rather than debug a browser, this observes
    // the state that actually changes. It also catches every route out at once: the
    // button, the backdrop, Esc, and anything added later.
    new MutationObserver(() => {
      if (!dialog.open) this.onClose();
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

    // A click on the backdrop closes - but only when the image is at fit. Zoomed in, a
    // stray tap outside the picture is far more likely to be a missed pan than an intent
    // to leave, and losing your place in a document is a bad thing to do by accident.
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog && this.scale === 1) dialog.close();
    });

    this.bindGestures();

    // Rotating a phone changes the stage under a zoomed page; without this the image can
    // be left parked outside its own bounds.
    window.addEventListener('resize', () => {
      if (this.dialog.open) { this.clampPan(); this.apply(); }
    });

    document.body.appendChild(dialog);
  }

  /* ------------------------------------------------------------- gestures */

  bindGestures() {
    const stage = this.stage;

    stage.addEventListener('pointerdown', (e) => {
      stage.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.moved = false;
      if (this.pointers.size === 2) this.pinchStart();
    });

    stage.addEventListener('pointermove', (e) => {
      const p = this.pointers.get(e.pointerId);
      if (!p) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.moved = true;
      p.x = e.clientX;
      p.y = e.clientY;

      if (this.pointers.size === 2) {
        this.pinchMove();
      } else if (this.scale > 1) {
        this.tx += dx;
        this.ty += dy;
        this.clampPan();
        this.apply();
      }
    });

    const end = (e) => {
      const wasSingle = this.pointers.size === 1;
      this.pointers.delete(e.pointerId);
      if (wasSingle && !this.moved) this.maybeDoubleTap(e);
    };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', (e) => this.pointers.delete(e.pointerId));

    stage.addEventListener('dblclick', (e) => {
      e.preventDefault();
      this.toggleZoom(e.clientX, e.clientY);
    });

    stage.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.zoomAt(this.scale * (e.deltaY < 0 ? 1.16 : 1 / 1.16), e.clientX, e.clientY);
      },
      { passive: false }
    );
  }

  pinchStart() {
    const [a, b] = [...this.pointers.values()];
    this.pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    this.pinchScale = this.scale;
  }

  pinchMove() {
    const [a, b] = [...this.pointers.values()];
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    if (!this.pinchDist) return;
    this.zoomAt(this.pinchScale * (dist / this.pinchDist), (a.x + b.x) / 2, (a.y + b.y) / 2);
  }

  maybeDoubleTap(e) {
    const now = performance.now();
    const near =
      this.lastTapPos &&
      Math.hypot(e.clientX - this.lastTapPos.x, e.clientY - this.lastTapPos.y) < DOUBLE_TAP_SLOP;
    if (now - this.lastTap < DOUBLE_TAP_MS && near) {
      this.toggleZoom(e.clientX, e.clientY);
      this.lastTap = 0;
    } else {
      this.lastTap = now;
      this.lastTapPos = { x: e.clientX, y: e.clientY };
    }
  }

  /* ----------------------------------------------------------------- zoom */

  toggleZoom(x, y) {
    // 2.6 rather than max: one tap should make the report readable, not drop you into a
    // corner of it. Repeat taps keep going in.
    this.zoomAt(this.scale > 1.05 ? 1 : 2.6, x, y);
  }

  zoomBy(factor) {
    const r = this.stage.getBoundingClientRect();
    this.zoomAt(this.scale * factor, r.left + r.width / 2, r.top + r.height / 2);
  }

  /** Zoom about a point, so the pixel under the finger stays under the finger. */
  zoomAt(next, clientX, clientY) {
    next = clamp(next, 1, MAX_SCALE);
    const r = this.stage.getBoundingClientRect();
    const cx = clientX - r.left - r.width / 2;
    const cy = clientY - r.top - r.height / 2;
    const k = next / this.scale;
    this.tx = cx - k * (cx - this.tx);
    this.ty = cy - k * (cy - this.ty);
    this.scale = next;
    if (this.scale === 1) { this.tx = 0; this.ty = 0; }
    this.clampPan();
    this.apply();
  }

  /**
   * The size the picture is actually drawn at inside its box.
   *
   * The image element fills the stage and uses object-fit, so its offsetWidth is the box,
   * not the picture - clamping against the box would let a portrait page be dragged half
   * a screen sideways into empty space. This works the contain maths out instead.
   */
  fittedSize() {
    // Two different boxes, and they are not the same one.
    //
    // The picture is fitted into the image element, which is inset from the stage so that
    // at fit it clears the two floating bars - the top of an SGS page is the report number
    // and nobody should have to pan to read it. Panning, though, is clamped against the
    // whole stage, because once you are zoomed in the bars are something to slide under
    // rather than a wall.
    const bw = this.img.offsetWidth;
    const bh = this.img.offsetHeight;
    const nw = this.img.naturalWidth || bw;
    const nh = this.img.naturalHeight || bh;
    const k = Math.min(bw / nw, bh / nh);
    return { w: nw * k, h: nh * k, stage: this.stage.getBoundingClientRect() };
  }

  /** Never let the image be dragged clear of the stage. */
  clampPan() {
    const f = this.fittedSize();
    const maxX = Math.max(0, (f.w * this.scale - f.stage.width) / 2);
    const maxY = Math.max(0, (f.h * this.scale - f.stage.height) / 2);
    this.tx = clamp(this.tx, -maxX, maxX);
    this.ty = clamp(this.ty, -maxY, maxY);
  }

  apply() {
    this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
    this.levelEl.textContent = Math.round(this.scale * 100) + '%';
    this.dialog.classList.toggle('is-zoomed', this.scale > 1.05);
  }

  reset() {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.apply();
  }

  /* -------------------------------------------------------------- paging */

  step(d) {
    if (this.items.length < 2) return;
    this.show((this.index + d + this.items.length) % this.items.length);
  }

  show(i) {
    this.index = i;
    const item = this.items[i];
    this.img.src = item.src;
    this.img.alt = item.alt || '';
    this.originalEl.href = item.src;
    this.captionEl.textContent = item.alt || '';
    this.titleEl.textContent =
      (this.title ? this.title + ' · ' : '') +
      (this.items.length > 1 ? `${i + 1} of ${this.items.length}` : '');
    const many = this.items.length > 1;
    this.prevBtn.hidden = !many;
    this.nextBtn.hidden = !many;
    this.reset();
  }

  /* --------------------------------------------------------------- modal */

  open(items, index, title) {
    this.items = items;
    this.title = title || '';
    this.show(index || 0);
    this.closed = false;
    if (!this.dialog.open) this.dialog.showModal();
    // iOS Safari still scrolls the page behind a modal dialog under some conditions.
    document.documentElement.classList.add('beeul-viewer-open');
  }

  /** Idempotent: the observer can fire for a state that is already torn down. */
  onClose() {
    if (this.closed) return;
    this.closed = true;
    document.documentElement.classList.remove('beeul-viewer-open');
    this.pointers.clear();
    // Drop the source so a large report page is not held in memory behind the scenes.
    this.img.removeAttribute('src');
  }

  onKey(e) {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowLeft': {
        e.preventDefault();
        const d = e.key === 'ArrowRight' ? 1 : -1;
        // Zoomed in, the arrows pan the page you are reading. At fit, they turn it.
        if (this.scale > 1.05) { this.tx -= d * 60; this.clampPan(); this.apply(); }
        else this.step(d);
        break;
      }
      case 'ArrowUp':
      case 'ArrowDown':
        if (this.scale > 1.05) {
          e.preventDefault();
          this.ty -= (e.key === 'ArrowDown' ? 1 : -1) * 60;
          this.clampPan();
          this.apply();
        }
        break;
      case '+': case '=': e.preventDefault(); this.zoomBy(1.6); break;
      case '-': e.preventDefault(); this.zoomBy(1 / 1.6); break;
      case '0': e.preventDefault(); this.reset(); break;
    }
  }
}

/* ------------------------------------------------------------------ wiring */

let viewer = null;
const getViewer = () => (viewer ||= new BeeulViewer());

/** Turn a container of <a href="full.jpg"><img></a> into one viewer group. */
function wireGroup(group, title) {
  const links = [...group.querySelectorAll('a[href]')].filter((a) => a.querySelector('img'));
  if (!links.length) return;

  const items = links.map((a) => ({
    src: a.getAttribute('href'),
    alt: a.querySelector('img')?.getAttribute('alt') || '',
  }));

  links.forEach((a, i) => {
    a.addEventListener('click', (e) => {
      // Leave the modified clicks alone - someone asking for a new tab should get one.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      getViewer().open(items, i, title);
    });
  });
  group.classList.add('beeul-viewer-wired');
}

/**
 * The photo wall has no links for wireGroup to find - it is a grid of <li><img></li> - so
 * each tile gets a transparent button and the set is handed over as one group.
 *
 * This lives here rather than in beeul-motion.js because it is viewer work, and because
 * splitting it across two deferred scripts made it depend on their execution order: the
 * motion file ran its init first, found no window.BeeulViewer yet, and silently built
 * nothing. Same file, no ordering to get wrong.
 */
function upgradePhotoWall(wall) {
  const tiles = [...wall.querySelectorAll('.beeul-wall__item')].filter((t) => t.querySelector('img'));
  if (tiles.length < 2) return;

  const items = tiles.map((tile) => ({ src: '', alt: tile.querySelector('img').getAttribute('alt') || '' }));

  tiles.forEach((tile, i) => {
    if (tile.querySelector('.beeul-wall__open')) return;
    const label = tile.querySelector('img').getAttribute('alt');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'beeul-wall__open';
    // An empty alt marks a decorative image, and inventing a name for it would be worse
    // than a generic one. Either way the control says what it does.
    button.setAttribute('aria-label', label ? `Open photograph: ${label}` : 'Open photograph');
    button.addEventListener('click', () => {
      // Read currentSrc at click time - it is the file the browser already chose from the
      // srcset and already has cached, and a resize may have changed which one that is.
      tiles.forEach((t, n) => {
        const el = t.querySelector('img');
        items[n].src = el.currentSrc || el.src;
      });
      getViewer().open(items, i, '');
    });

    tile.appendChild(button);
    tile.classList.add('beeul-wall__item--openable');
  });
}

function init(root = document) {
  if (typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;

  for (const g of root.querySelectorAll('[data-beeul-viewer-group]')) {
    if (g.classList.contains('beeul-viewer-wired')) continue;
    wireGroup(g, g.getAttribute('data-beeul-viewer-title') || '');
  }

  for (const wall of root.querySelectorAll('.beeul-wall')) upgradePhotoWall(wall);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (e) => init(e.target));

window.BeeulViewer = { open: (items, i, title) => getViewer().open(items, i, title), init };
